import React, { useState } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Download, ArrowUpFromLine, CheckCircle2, XCircle, Clock, Loader2, Plus, History, RefreshCw, Wallet, TrendingUp } from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import StatusBadge from "@/shared/components/merchant/StatusBadge";
import WithdrawalForm from "@/shared/components/withdrawals/WithdrawalForm";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

/* ── Hover stat card ── */
function StatCard({ label, value, color, delay }) {
  const [h, setH] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: "linear-gradient(135deg,rgba(13,27,46,.88) 0%,rgba(15,30,55,.88) 100%)",
        border: `1px solid ${h ? color + "50" : color + "22"}`,
        borderRadius: "18px",
        padding: "20px",
        position: "relative",
        overflow: "hidden",
        transition: "all .45s cubic-bezier(0.34,1.56,0.64,1)",
        transform: h ? "translateY(-8px) scale(1.03)" : "translateY(0) scale(1)",
        boxShadow: h ? `0 20px 48px ${color}28, 0 0 0 1px ${color}22` : "0 2px 10px rgba(0,0,0,.28)",
        cursor: "default",
      }}
    >
      {/* radial glow */}
      <div style={{ position:"absolute", inset:0, background: h ? `radial-gradient(circle at 80% 20%, ${color}18 0%, transparent 65%)` : "none", pointerEvents:"none", transition:"all .4s ease" }} />
      {/* top accent line */}
      {h && <div style={{ position:"absolute", top:0, left:0, right:0, height:"2px", background:`linear-gradient(90deg,transparent,${color}90,transparent)`, borderRadius:"18px 18px 0 0" }} />}
      {/* bottom accent line on hover */}
      {h && <div style={{ position:"absolute", bottom:0, left:"20%", right:"20%", height:"1px", background:`linear-gradient(90deg,transparent,${color}60,transparent)` }} />}
      <p style={{ fontSize:"10px", color:"#64748b", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", marginBottom:"10px" }}>{label}</p>
      <p style={{ fontSize:"24px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.025em" }}>{value}</p>
    </motion.div>
  );
}

/* ── Tab button ── */
function TabBtn({ active, onClick, icon: Icon, label }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        padding: "10px 22px",
        borderRadius: "12px",
        border: "none",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 600,
        letterSpacing: "0.01em",
        transition: "all .4s cubic-bezier(0.34,1.56,0.64,1)",
        background: active
          ? "linear-gradient(135deg,rgba(245,158,11,.3) 0%,rgba(249,115,22,.22) 100%)"
          : h ? "rgba(245,158,11,.08)" : "transparent",
        color: active ? "#fbbf24" : h ? "#d97706" : "#64748b",
        boxShadow: active ? "0 4px 18px rgba(245,158,11,.22), inset 0 0 0 1px rgba(245,158,11,.32)" : "none",
        transform: active ? "scale(1.03)" : h ? "scale(1.01)" : "scale(1)",
      }}
    >
      <Icon style={{ width:"15px", height:"15px", transition:"transform .35s cubic-bezier(0.34,1.56,0.64,1)", transform: h && !active ? "rotate(-5deg) scale(1.12)" : "none" }} />
      {label}
    </button>
  );
}

export default function ResellerWithdrawals() {
  const [activeTab, setActiveTab] = useState("new");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTx, setSelectedTx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sf, setSf] = useState(false);
  const { t, language } = useLanguage();
  const resellerName = "Asia Gaming Network";

  // ── exact original query ──
  const { data: transactions = [], isLoading, refetch } = useQuery({
    queryKey: ["reseller-withdrawals"],
    queryFn: async () => [],
  });

  // ── exact original filter ──
  const filteredTransactions = transactions.filter(tx => {
    if (search && !tx.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && tx.status !== statusFilter) return false;
    return true;
  });

  // ── exact original stats ──
  const stats = React.useMemo(() => {
    const success = transactions.filter(t => t.status === "success");
    const pending = transactions.filter(t => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((sum, t) => sum + (t.amount || 0), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, t) => sum + (t.amount || 0), 0),
    };
  }, [transactions]);

  // ── exact original handler ──
  const handleWithdrawalSubmit = async (data) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    toast.success(`Withdrawal request submitted successfully! Type: ${data.type}`);
  };

  const STATUSES = ["all", "success", "pending", "processing", "failed"];

  return (
    <ResellerLayout currentPage="ResellerWithdrawals" resellerName={resellerName}>
      <style>{`
        @keyframes wdSpin { to { transform: rotate(360deg); } }
        @keyframes wdPulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes wdSlide { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div style={{ maxWidth: "1400px" }}>

        {/* ── Header ── */}
        <motion.div initial={{ opacity:0, y:-14 }} animate={{ opacity:1, y:0 }} transition={{ duration:.4, ease:[0.34,1.56,0.64,1] }} style={{ marginBottom:"28px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
            <div style={{ width:"46px", height:"46px", borderRadius:"14px", background:"linear-gradient(135deg,#f59e0b,#f97316)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(245,158,11,.5)", flexShrink:0 }}>
              <ArrowUpFromLine style={{ width:"21px", height:"21px", color:"#fff" }} />
            </div>
            <div>
              <h1 style={{ fontSize:"24px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.025em", margin:0 }}>
                {t("withdrawals") || "Withdrawals"}
              </h1>
              <p style={{ fontSize:"13px", color:"#475569", margin:0 }}>{t("requestPayoutsViaBankUsdt") || "Request payouts via Bank or USDT"}</p>
            </div>
          </div>
        </motion.div>

        {/* ── Tab pill bar ── */}
        <motion.div
          initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:.08, duration:.4 }}
          style={{ display:"inline-flex", background:"rgba(8,14,26,.88)", border:"1px solid rgba(245,158,11,.13)", borderRadius:"16px", padding:"5px", gap:"4px", marginBottom:"28px" }}
        >
          <TabBtn active={activeTab==="new"} onClick={()=>setActiveTab("new")} icon={Plus} label={t("newWithdrawal")||"New Withdrawal"} />
          <TabBtn active={activeTab==="history"} onClick={()=>setActiveTab("history")} icon={History} label={t("history")||"History"} />
        </motion.div>

        {/* ── Tab Content ── */}
        <AnimatePresence mode="wait">

          {/* NEW WITHDRAWAL TAB */}
          {activeTab === "new" && (
            <motion.div
              key="new"
              initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }}
              transition={{ duration:.38, ease:[0.34,1.56,0.64,1] }}
            >
              <div style={{
                background:"linear-gradient(135deg,rgba(13,27,46,.94) 0%,rgba(15,30,55,.94) 100%)",
                border:"1px solid rgba(245,158,11,.18)",
                borderRadius:"22px",
                padding:"28px",
                position:"relative",
                overflow:"hidden",
                boxShadow:"0 12px 50px rgba(0,0,0,.38)",
              }}>
                {/* decorative top line */}
                <div style={{ position:"absolute", top:0, left:0, right:0, height:"2px", background:"linear-gradient(90deg,transparent,rgba(245,158,11,.6),rgba(249,115,22,.5),transparent)" }} />
                {/* ambient glow */}
                <div style={{ position:"absolute", top:0, right:0, width:"260px", height:"260px", background:"radial-gradient(circle at top right,rgba(245,158,11,.1) 0%,transparent 70%)", pointerEvents:"none" }} />
                <div style={{ position:"absolute", bottom:0, left:0, width:"200px", height:"200px", background:"radial-gradient(circle at bottom left,rgba(249,115,22,.07) 0%,transparent 70%)", pointerEvents:"none" }} />

                <div style={{ marginBottom:"22px", position:"relative" }}>
                  <h2 style={{ fontSize:"16px", fontWeight:700, color:"#f0f9ff", margin:"0 0 4px", letterSpacing:"-0.01em" }}>
                    {t("newWithdrawal") || "New Withdrawal Request"}
                  </h2>
                  <p style={{ fontSize:"13px", color:"#64748b", margin:0 }}>
                    {t("requestPayoutsViaBankUsdt") || "Submit a payout via Bank Transfer or USDT"}
                  </p>
                </div>

                {/* ── exact original WithdrawalForm with all props ── */}
                <WithdrawalForm
                  onSubmit={handleWithdrawalSubmit}
                  submitting={submitting}
                  stats={stats}
                  accentColor="violet"
                />
              </div>
            </motion.div>
          )}

          {/* HISTORY TAB */}
          {activeTab === "history" && (
            <motion.div
              key="history"
              initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }}
              transition={{ duration:.38, ease:[0.34,1.56,0.64,1] }}
            >
              {/* ── exact original summary stats ── */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:"14px", marginBottom:"24px" }}>
                <StatCard label={t("totalWithdrawn")||"Total Withdrawn"} value={`฿${stats.total.toLocaleString()}`} color="#f59e0b" delay={0} />
                <StatCard label={t("completed")||"Completed"} value={transactions.filter(t=>t.status==="success").length} color="#10b981" delay={0.05} />
                <StatCard label={t("pending")||"Pending"} value={stats.pendingCount} color="#f59e0b" delay={0.1} />
                <StatCard label="Total" value={transactions.length} color="#38bdf8" delay={0.15} />
              </div>

              {/* ── filters row ── */}
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.18 }}
                style={{ display:"flex", flexWrap:"wrap", gap:"10px", marginBottom:"18px", alignItems:"center" }}>

                {/* search */}
                <div style={{ position:"relative", flex:"1 1 200px", maxWidth:"360px" }}>
                  <Search style={{ position:"absolute", left:"14px", top:"50%", transform:"translateY(-50%)", width:"15px", height:"15px", color: sf?"#f59e0b":"#64748b", transition:"color .2s" }} />
                  <input
                    value={search}
                    onChange={e=>setSearch(e.target.value)}
                    onFocus={()=>setSf(true)} onBlur={()=>setSf(false)}
                    placeholder={`${t("search")||"Search"}...`}
                    style={{ width:"100%", padding:"10px 14px 10px 40px", borderRadius:"12px", background:"rgba(13,27,46,.85)", border:`1px solid ${sf?"rgba(245,158,11,.45)":"rgba(245,158,11,.13)"}`, color:"#e2e8f0", fontSize:"13px", outline:"none", transition:"all .3s ease", boxShadow: sf?"0 0 0 3px rgba(245,158,11,.1)":"none" }}
                  />
                </div>

                {/* exact original status filter - now pill style */}
                <div style={{ display:"flex", gap:"6px", flexWrap:"wrap" }}>
                  {STATUSES.map(s => {
                    const active = statusFilter === s;
                    return (
                      <button key={s} onClick={()=>setStatusFilter(s)} style={{
                        padding:"8px 14px", borderRadius:"20px", fontSize:"12px", fontWeight:600, cursor:"pointer", textTransform:"capitalize",
                        transition:"all .4s cubic-bezier(0.34,1.56,0.64,1)",
                        background: active ? "linear-gradient(135deg,rgba(245,158,11,.28),rgba(249,115,22,.22))" : "rgba(13,27,46,.7)",
                        border: active ? "1px solid rgba(245,158,11,.45)" : "1px solid rgba(245,158,11,.1)",
                        color: active ? "#fbbf24" : "#64748b",
                        transform: active ? "scale(1.07)" : "scale(1)",
                        boxShadow: active ? "0 4px 16px rgba(245,158,11,.22)" : "none",
                      }}>
                        {s==="all"?"All":s.charAt(0).toUpperCase()+s.slice(1)}
                      </button>
                    );
                  })}
                </div>

                <div style={{ display:"flex", gap:"8px", marginLeft:"auto" }}>
                  {/* export button - exact original feature */}
                  <button
                    style={{ display:"flex", alignItems:"center", gap:"6px", padding:"9px 14px", borderRadius:"12px", background:"rgba(56,189,248,.08)", border:"1px solid rgba(56,189,248,.2)", color:"#38bdf8", fontSize:"12px", fontWeight:600, cursor:"pointer", transition:"all .38s cubic-bezier(0.34,1.56,0.64,1)" }}
                    onMouseEnter={e=>{ e.currentTarget.style.background="rgba(56,189,248,.18)"; e.currentTarget.style.transform="translateY(-2px) scale(1.05)"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background="rgba(56,189,248,.08)"; e.currentTarget.style.transform="none"; }}
                  >
                    <Download style={{ width:"14px", height:"14px" }} />{t("export")||"Export"}
                  </button>
                  {/* refresh */}
                  <button onClick={()=>refetch()}
                    style={{ width:"40px", height:"40px", borderRadius:"12px", background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.2)", color:"#f59e0b", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all .45s cubic-bezier(0.34,1.56,0.64,1)", flexShrink:0 }}
                    onMouseEnter={e=>{ e.currentTarget.style.transform="rotate(180deg) scale(1.15)"; e.currentTarget.style.background="rgba(245,158,11,.2)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(245,158,11,.3)"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.transform="rotate(0) scale(1)"; e.currentTarget.style.background="rgba(245,158,11,.08)"; e.currentTarget.style.boxShadow="none"; }}
                  >
                    <RefreshCw style={{ width:"15px", height:"15px" }} />
                  </button>
                </div>
              </motion.div>

              {/* ── Table ── */}
              <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:.24 }}
                style={{ background:"linear-gradient(135deg,rgba(13,27,46,.93) 0%,rgba(15,30,55,.93) 100%)", border:"1px solid rgba(245,158,11,.12)", borderRadius:"20px", overflow:"hidden" }}>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr style={{ borderBottom:"1px solid rgba(245,158,11,.08)", background:"rgba(8,14,26,.65)" }}>
                        {[t("transaction")||"Transaction", t("merchant")||"Merchant", t("method")||"Method", t("amount")||"Amount", t("status")||"Status", t("date")||"Date"].map((h,i)=>(
                          <th key={i} style={{ padding:"13px 18px", textAlign: i===3?"right":"left", fontSize:"10px", fontWeight:700, color:"#334155", textTransform:"uppercase", letterSpacing:"0.1em", whiteSpace:"nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr><td colSpan={6} style={{ textAlign:"center", padding:"72px 24px", color:"#475569" }}>
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"14px" }}>
                            <RefreshCw style={{ width:"30px", height:"30px", color:"#f59e0b", animation:"wdSpin 1s linear infinite" }} />
                            <span style={{ fontSize:"13px" }}>{t("loading")||"Loading…"}</span>
                          </div>
                        </td></tr>
                      ) : filteredTransactions.length === 0 ? (
                        <tr><td colSpan={6} style={{ textAlign:"center", padding:"80px 24px", color:"#334155" }}>
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"16px" }}>
                            <ArrowUpFromLine style={{ width:"56px", height:"56px", color:"#1e293b" }} />
                            <p style={{ margin:0, fontSize:"14px" }}>{t("noWithdrawalsFound")||"No withdrawals found"}</p>
                            <button onClick={()=>setActiveTab("new")}
                              style={{ padding:"10px 22px", borderRadius:"12px", background:"linear-gradient(135deg,rgba(245,158,11,.25),rgba(249,115,22,.2))", border:"1px solid rgba(245,158,11,.38)", color:"#fbbf24", fontSize:"13px", fontWeight:600, cursor:"pointer", transition:"all .38s cubic-bezier(0.34,1.56,0.64,1)" }}
                              onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-3px) scale(1.06)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(245,158,11,.25)"; }}
                              onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}
                            >+ {t("newWithdrawal")||"New Withdrawal"}</button>
                          </div>
                        </td></tr>
                      ) : (
                        /* ── exact original map logic with original components ── */
                        filteredTransactions.slice(0, 50).map((tx, i) => (
                          <motion.tr
                            key={tx.id}
                            initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.02 }}
                            onClick={() => setSelectedTx(tx)}
                            style={{ borderBottom:"1px solid rgba(245,158,11,.04)", cursor:"pointer", transition:"background .2s ease" }}
                            onMouseEnter={e=>{
                              e.currentTarget.style.background="rgba(245,158,11,.05)";
                              e.currentTarget.querySelector?.(".tx-icon")?.style && (e.currentTarget.querySelector(".tx-icon").style.transform="rotate(-5deg) scale(1.1)");
                            }}
                            onMouseLeave={e=>{
                              e.currentTarget.style.background="transparent";
                            }}
                          >
                            <td style={{ padding:"14px 18px" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                                <div className="tx-icon" style={{ width:"36px", height:"36px", borderRadius:"10px", background:"rgba(245,158,11,.12)", border:"1px solid rgba(245,158,11,.22)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"transform .35s cubic-bezier(0.34,1.56,0.64,1)" }}>
                                  <ArrowUpFromLine style={{ width:"15px", height:"15px", color:"#f59e0b" }} />
                                </div>
                                <div>
                                  <p style={{ margin:0, fontFamily:"monospace", fontSize:"12px", color:"#38bdf8", fontWeight:600 }}>{tx.transaction_id?.slice(0, 10)}...</p>
                                  <p style={{ margin:0, fontSize:"11px", color:"#475569" }}>{tx.merchant_code}</p>
                                </div>
                              </div>
                            </td>
                            {/* exact original merchant_code column */}
                            <td style={{ padding:"14px 18px" }}>
                              <span style={{ fontSize:"13px", color:"#94a3b8" }}>{tx.merchant_code || t("nA") || "N/A"}</span>
                            </td>
                            {/* exact original payment_method badge */}
                            <td style={{ padding:"14px 18px" }}>
                              <span style={{ fontSize:"11px", color:"#38bdf8", background:"rgba(56,189,248,.08)", padding:"3px 9px", borderRadius:"6px", border:"1px solid rgba(56,189,248,.18)", fontWeight:600 }}>
                                {tx.payment_method === "bank_transfer" ? t("bank") || "Bank" : "USDT"}
                              </span>
                            </td>
                            {/* exact original amount */}
                            <td style={{ padding:"14px 18px", textAlign:"right" }}>
                              <span style={{ fontSize:"15px", fontWeight:700, color:"#f59e0b" }}>฿{tx.amount?.toLocaleString()}</span>
                            </td>
                            {/* exact original StatusBadge component */}
                            <td style={{ padding:"14px 18px" }}>
                              <StatusBadge status={tx.status} />
                            </td>
                            {/* exact original date format */}
                            <td style={{ padding:"14px 18px" }}>
                              <span style={{ fontSize:"12px", color:"#64748b" }}>
                                {tx.created_date ? format(new Date(tx.created_date), language === "th" ? "d MMM, HH:mm" : "MMM d, HH:mm", { locale: language === "th" ? th : enUS }) : t("nA")}
                              </span>
                            </td>
                          </motion.tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── exact original TransactionDetailPanel ── */}
        <TransactionDetailPanel transaction={selectedTx} open={!!selectedTx} onClose={() => setSelectedTx(null)} />
      </div>
    </ResellerLayout>
  );
}